import { create } from 'zustand';
import { UserWithMentor } from '@/types/mentor';

type MentorSearchState = {
  searchText: string;
  setSearchText: (text: string) => void;

  filters: any;
  setFilters: (filters: any) => void;

  modalValues: any;
  setModalValues: (values: any) => void;

  modalOpened: boolean;
  setModalOpened: (opened: boolean) => void;
  openModal: () => void;
  closeModal: () => void;

  users: UserWithMentor[];
  setUsers: (users: UserWithMentor[]) => void;

  cursor: string | null;
  setCursor: (cursor: string | null) => void;

  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  placeholderIndex: number;
  setPlaceholderIndex: (idx: number) => void;

  hasSearched: () => boolean;

  rotatingExamples: string[];

  fetchMentors: (reset?: boolean, overrideFilters?: any) => Promise<void>;
  handleLLMSearch: () => Promise<void>;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const useMentorSearchStore = create<MentorSearchState>((set, get) => ({
  searchText: '',
  setSearchText: (searchText) => set({ searchText }),

  filters: {},
  setFilters: (filters) => set({ filters }),

  modalValues: {
    expertise: [] as string[],
    location: '',
    distance: undefined,
    name: '',
    bio: '',
    job: '',
    timezone: '',
    skills: [],
  },
  setModalValues: (values) =>
    set((state) => ({
      modalValues: {
        ...state.modalValues,
        ...values,
      },
    })),

  modalOpened: false,
  setModalOpened: (opened) => set({ modalOpened: opened }),
  openModal: () => set({ modalOpened: true }),
  closeModal: () => set({ modalOpened: false }),

  users: [],
  setUsers: (users) => set({ users }),

  cursor: null,
  setCursor: (cursor) => set({ cursor }),

  isLoading: false,
  setIsLoading: (isLoading) => set({ isLoading }),

  placeholderIndex: 0,
  setPlaceholderIndex: (idx) => set({ placeholderIndex: idx }),

  hasSearched: () => {
    const { filters } = get();
    return (
      (Array.isArray(filters.expertise) && filters.expertise.length > 0) ||
      !!filters.location ||
      !!filters.name
    );
  },

  rotatingExamples: [
    'I want to learn frontend development…',
    'Looking for a mentor in AI near me…',
    'Someone who knows growth marketing…',
    'I need help with UX design in NZ…',
  ],

  fetchMentors: async (reset = false, overrideFilters) => {
    set({ isLoading: true });
    const { filters, cursor, users } = get();
    const query = new URLSearchParams();
    const activeFilters = overrideFilters ?? filters;

    Object.entries(activeFilters).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        if (Array.isArray(val)) {
          if (val.length > 0) query.append(key, val.join(','));
        } else {
          query.append(key, String(val));
        }
      }
    });

    if (!reset && cursor) query.append('cursor', cursor);

    const res = await fetch(`/api/mentors/search?${query.toString()}`);
    const data = await res.json();

    set({
      users: reset ? data.data : [...users, ...data.data],
      cursor: data.nextCursor,
      isLoading: false,
    });
  },

  handleLLMSearch: async () => {
    set({ isLoading: true });
    const { searchText, setFilters, setModalValues, setCursor, fetchMentors } = get();

    const res = await fetch('/api/mentors/generateFilters', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ input: searchText }),
    });
    const parsed = await res.json();

    if (parsed.useCurrentLocation && typeof window !== 'undefined' && navigator.geolocation) {
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        parsed.latitude = pos.coords.latitude;
        parsed.longitude = pos.coords.longitude;
        parsed.distance ??= 50;
      } catch (err) {
        console.warn('Geolocation failed or was denied.', err);
      }
    }

    if (parsed.latitude && parsed.longitude && !parsed.location) {
      parsed.location = 'Your current location';
    }

    if (parsed.expertise && !Array.isArray(parsed.expertise)) {
      parsed.expertise = [parsed.expertise];
    } else if (!parsed.expertise) {
      parsed.expertise = [];
    }

    setFilters(parsed);
    setModalValues(parsed);
    setCursor(null);
    await fetchMentors(true, parsed);
    set({ isLoading: false });
  },

  handleKeyPress: (e) => {
    if (e.key === 'Enter') {
      get().handleLLMSearch();
    }
  },
}));
